import React, { useState, useRef, useEffect } from 'react';
import QRCode from 'qrcode';

export default function QRCodeGenerator() {
  const [text, setText] = useState('');
  const [qrType, setQrType] = useState('url');
  const [size, setSize] = useState(200);
  const [color, setColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [generated, setGenerated] = useState(false);
  const [history, setHistory] = useState([]);
  
  const canvasRef = useRef(null);
  
  // 미리 정의된 QR 코드 유형
  const qrTypes = [
    { id: 'url', name: 'URL', placeholder: 'https://example.com', prefix: '' },
    { id: 'email', name: '이메일', placeholder: 'example@email.com', prefix: 'mailto:' },
    { id: 'phone', name: '전화번호', placeholder: '01012345678', prefix: 'tel:' },
    { id: 'sms', name: 'SMS', placeholder: '01012345678', prefix: 'sms:' },
    { id: 'text', name: '일반 텍스트', placeholder: '텍스트를 입력하세요', prefix: '' },
    { id: 'wifi', name: 'WiFi', placeholder: 'SSID, 비밀번호', prefix: '' },
  ];
  
  // QR 코드 생성
  const generateQRCode = () => {
    if (!text) return;
    
    let content = text;
    const type = qrTypes.find(t => t.id === qrType);
    
    // 유형별 특수 처리
    if (type.prefix) {
      content = `${type.prefix}${text}`;
    } else if (qrType === 'wifi') {
      // WiFi 형식: WIFI:S:<SSID>;T:<WPA|WEP|>;P:<password>;;
      const [ssid, password] = text.split(',').map(s => s.trim());
      content = `WIFI:S:${ssid};T:WPA;P:${password};;`;
    }
    
    const options = {
      width: size,
      height: size,
      margin: 1,
      color: {
        dark: color,
        light: bgColor
      }
    };
    
    QRCode.toCanvas(canvasRef.current, content, options, (error) => {
      if (error) {
        console.error('QR 코드 생성 오류:', error);
        return;
      }
      
      setGenerated(true);
      
      // 기록 저장
      const newHistoryItem = {
        type: qrType,
        content: text,
        timestamp: new Date().toISOString()
      };
      
      setHistory(prev => [newHistoryItem, ...prev.slice(0, 9)]);
    });
  };
  
  // QR 코드 다운로드
  const downloadQRCode = () => {
    if (!generated) return;
    
    const canvas = canvasRef.current;
    const image = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    
    link.href = image;
    link.download = `qrcode-${qrType}-${new Date().getTime()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // 유형 변경 시 placeholder 업데이트
  const getPlaceholder = () => {
    const type = qrTypes.find(t => t.id === qrType);
    return type ? type.placeholder : '';
  };
  
  // 유형 이름 가져오기
  const getTypeName = (typeId) => {
    const type = qrTypes.find(t => t.id === typeId);
    return type ? type.name : typeId;
  };
  
  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">QR 코드 생성기</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  QR 코드 유형
                </label>
                <select
                  value={qrType}
                  onChange={(e) => setQrType(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {qrTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {qrType === 'url' ? 'URL' : 
                   qrType === 'email' ? '이메일 주소' : 
                   qrType === 'phone' ? '전화번호' : 
                   qrType === 'wifi' ? 'WiFi 정보 (SSID, 비밀번호)' : '텍스트'}
                </label>
                <input
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={getPlaceholder()}
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  QR 코드 크기 (픽셀)
                </label>
                <input
                  type="range"
                  min="100"
                  max="400"
                  step="10"
                  value={size}
                  onChange={(e) => setSize(parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="text-sm text-gray-500 mt-1">{size} x {size} 픽셀</div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    QR 코드 색상
                  </label>
                  <div className="flex items-center">
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="h-8 w-8 border border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-500">{color}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    배경 색상
                  </label>
                  <div className="flex items-center">
                    <input
                      type="color"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="h-8 w-8 border border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-500">{bgColor}</span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={generateQRCode}
                disabled={!text}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
              >
                QR 코드 생성
              </button>
            </div>
          </div>
        </div>
        
        <div>
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 sm:p-6 flex flex-col items-center">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">생성된 QR 코드</h3>
              
              <div className="border border-gray-200 rounded-md p-2 bg-white mb-4">
                <canvas ref={canvasRef} className="mx-auto"></canvas>
              </div>
              
              {generated && (
                <button
                  onClick={downloadQRCode}
                  className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  QR 코드 다운로드
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {history.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium mb-4">최근 생성 기록</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    유형
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    내용
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    생성 시간
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getTypeName(item.type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                      {item.content}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(item.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
} 